import { uiText, useUiLanguage } from '@/i18n/interface';
import React from 'react';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { useTaigaDojoAccess } from '@/hooks/useTaigaDojoAccess';
import { useCurrentDojo } from '@/hooks/useCurrentDojo';
import { useAuth } from '@/contexts/AuthContext';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { PremiumUpgradeCard } from './PremiumUpgradeCard';

interface PremiumFeatureGateProps {
  children: React.ReactNode;
  featureName: string;
  description: string;
  customAccessCheck?: boolean;
}

export const PremiumFeatureGate: React.FC<PremiumFeatureGateProps> = ({ children, featureName, description, customAccessCheck }) => {
  useUiLanguage();
  const { hasPremiumAccess, loading } = usePremiumAccess();
  const { profile } = useAuth();
  const { isTrialExpired, isOnTrial, isTaigaDojo } = useTrialStatus();
  const { data: dojo } = useCurrentDojo();

  // Always allow access for Taiga Dojo users
  if (isTaigaDojo) {
    return <>{children}</>;
  }

  // Check trial status from dojo
  const isInTrial = dojo?.plan === 'trial' && dojo?.is_active;
  const trialStart = dojo?.trial_start ? new Date(dojo.trial_start) : null;
  const trialDays = 14;
  const trialEnd = trialStart ? new Date(trialStart.getTime() + (trialDays * 24 * 60 * 60 * 1000)) : null;
  const isTrialActive = trialEnd ? new Date() <= trialEnd : false;

  // During active trial, give FULL ACCESS
  if (isInTrial && isTrialActive) {
    return children;
  }

  // Check if user has premium access
  if (hasPremiumAccess === null || loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Use custom access check if provided, otherwise use premium access
  const effectiveAccess = customAccessCheck !== undefined ? customAccessCheck : hasPremiumAccess;
  const isAdmin = profile?.role === 'admin' || profile?.role === 'superadmin';

  console.log('🚨 PremiumFeatureGate Access Check:', {
    featureName: featureName,
    customAccessCheck: customAccessCheck,
    hasPremiumAccess: hasPremiumAccess,
    isOnTrial: isOnTrial,
    isTrialExpired: isTrialExpired,
    isTaigaDojo: isTaigaDojo,
    isInTrial: isInTrial,
    isTrialActive: isTrialActive,
    effectiveAccess: effectiveAccess,
    customAccessCheckProvided: customAccessCheck !== undefined,
    loading: loading
  });

  if (!effectiveAccess) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <h2 className="text-xl font-bold text-gray-700 mb-4">
          {featureName} {' '}{uiText("is a Premium Feature")}</h2>
        <p className="text-gray-600 mb-4">{description}</p>
        {isAdmin ? (
          <a
            href="https://www.taiga.io/pricing"
            className="bg-primary hover:bg-primary/90 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {uiText("Upgrade Now")}</a>
        ) : (
          <p className="text-gray-500 text-sm text-center">
            {uiText("This feature requires a premium subscription. Please contact your dojo administrator.")}</p>
        )}
      </div>
    );
  }

  return <>{children}</>;
};